import { FC, useMemo, useState } from 'react'
import {
  DataPoint,
  DataPointSkeleton,
  Highlights,
  HStack,
  Search,
  Token,
  VStack,
  Widget,
} from '@revolut-internal/ui-kit'
import { debounce } from 'lodash'

import { getFullUserName } from 'lib/utils'
import { getInputLoadingState } from 'utils/helpers'
import { useAssistantPlaygroundDialogues } from 'pages/ChatDebug/hooks'

import { AssistantConversationsTable } from './components'

export const AssistantTab: FC = () => {
  const [searchValue, setSearchValue] = useState('')
  const [debouncedSearchValue, setDebouncedSearchValue] = useState('')
  const { data, totalDialogues, isError, isLoading } = useAssistantPlaygroundDialogues()

  const debouncedSearch = useMemo(
    () =>
      debounce((newValue: string) => {
        setDebouncedSearchValue(newValue)
      }, 500),
    [],
  )

  const handleSearch = (newValue: string) => {
    setSearchValue(newValue)
    debouncedSearch(newValue)
  }

  const filteredData = useMemo(() => {
    return data.filter(entry => {
      const fullName = getFullUserName(entry.owner ?? null)
      return fullName
        .toLocaleLowerCase()
        .includes(debouncedSearchValue.toLocaleLowerCase())
    })
  }, [data, debouncedSearchValue])

  return (
    <Widget p="s-24" mt="s-16">
      <VStack space="s-16">
        <HStack width={350}>
          <Search
            value={searchValue}
            aria-label="Search user"
            placeholder="Search user"
            onChange={handleSearch}
            disabled={isError || isLoading || data.length === 0}
          />
        </HStack>

        <Highlights variant="tabs" defaultValue="playground">
          {isLoading ? (
            <DataPointSkeleton style={{ background: Token.color.searchBackground }} />
          ) : (
            <DataPoint to="playground">
              <DataPoint.Value>{totalDialogues}</DataPoint.Value>
              <DataPoint.Label>Playground</DataPoint.Label>
            </DataPoint>
          )}
        </Highlights>

        <AssistantConversationsTable
          data={filteredData}
          loadingState={getInputLoadingState(isLoading, isError)}
        />
      </VStack>
    </Widget>
  )
}
